'use client';

import { useState, useEffect, useMemo, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input, Button, Alert, Avatar } from '@heroui/react';
import { Spinner } from '@heroui/spinner';
import { FaEnvelope, FaArrowRight, FaChevronRight } from 'react-icons/fa6';
import {
  ContactType,
  ContactRoleType,
  Quote,
  ServWithMods,
} from '@/util/types/serverTypes';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { ContactFormData, useScheduler } from '../SchedulerContext';
import { useStepNavigation } from '../StepNavigationContext';
import * as Icons from 'react-icons/fa6';
import { motion } from 'framer-motion';
import { parsePhoneNumber } from 'libphonenumber-js';
import ContactFormFields from '../components/ContactFormFields';
import DraftSelectionModal from '../components/DraftSelectionModal';
import { useDraftQuotes } from '../hooks/useDraftQuotes';
import { useCheckExistingQuotes } from '../hooks/useCheckExistingQuotes';
import { saveDraftToStorage } from '../utils/draftStorage';
import { formatPhoneForDisplay } from '@/util/functions/formatPhoneForDisplay';
import { getRoleDescription } from '../utils/getRoleDescription';
import { validateSingleEmailAddress } from '@/util/functions/validation/isSingleEmailAddress';

type ContactFormProps = {
  hasValidQuestions?: boolean;
};

export default function ContactForm({
  hasValidQuestions = false,
}: ContactFormProps) {
  const {
    contact,
    setContact,
    selectedRole,
    selectedRoleIds,
    setSelectedRole,
    setSelectedRoleIds,
    selectedPrimaryService,
    isLoadingDraft,
    draftQuoteId,
    setDraftQuoteId,
    setDraftQuoteSlug,
    setShouldResumeDraft,
    startNewQuote,
    isCheckingDrafts,
    setIsCheckingDrafts,
    jwtToken,
    companyId,
    companyStyles,
    contactRoles,
    services,
  } = useScheduler();
  const { nextStep } = useStepNavigation();
  const [email, setEmail] = useState('');
  const [emailSearchError, setEmailSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showFullForm, setShowFullForm] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [foundContact, setFoundContact] = useState<ContactType | null>(null);
  const [ccFoundContacts, setCcFoundContacts] = useState<ContactType[]>([]);
  const [showCcConfirmation, setShowCcConfirmation] = useState(false);
  const [isCheckingRoles, setIsCheckingRoles] = useState(false);
  const [rolesJustFetched, setRolesJustFetched] = useState(false);

  // Quote checking hook - centralized with caching
  const {
    quotes,
    isLoading: isLoadingQuotes,
    fetchQuotes,
    clearQuotes,
  } = useDraftQuotes(jwtToken, companyId);
  // Also use checkQuotes directly for synchronous quote fetching
  const { checkQuotes } = useCheckExistingQuotes(jwtToken, companyId);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quotesForModal, setQuotesForModal] = useState<Quote[]>([]);

  // React Hook Form for full contact form
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<ContactFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    mode: 'onChange', // Validate on every change to show errors immediately
  });

  // Hydrate form from context when draft is loaded
  useEffect(() => {
    if (contact && !isLoadingDraft) {
      reset({
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        email: contact.email || '',
        phone: formatPhoneForDisplay(contact.phone) || '',
      });
      setEmail(contact.email || '');
      setFoundContact(contact);
      setShowCcConfirmation(false);
      setCcFoundContacts([]);
      setShowFullForm(true);
    }
  }, [contact, selectedRole, isLoadingDraft, reset]);

  // Memoize available roles to prevent unnecessary recalculations
  // ATT-193: Sort so Client's Agent appears first
  const availableRoles = useMemo(() => {
    if (
      selectedRoleIds.length === 0 ||
      (selectedRoleIds.length === 1 && selectedRoleIds[0] === 'search-mode')
    ) {
      return [];
    }
    const filtered = contactRoles.filter((role) => selectedRoleIds.includes(role._id));
    const isClientsAgent = (r: ContactRoleType) => {
      const name = r.name?.toLowerCase() || '';
      return name.includes("client's agent") || name.includes('clients agent');
    };
    return filtered.sort((a, b) =>
      isClientsAgent(a) && !isClientsAgent(b) ? -1 : !isClientsAgent(a) && isClientsAgent(b) ? 1 : 0
    );
  }, [selectedRoleIds, contactRoles]);

  // Handle role selection logic when availableRoles changes
  useEffect(() => {
    if (availableRoles.length === 0) {
      setShowRoleSelection(false);
      return;
    }

    // Auto-select if only one role
    if (availableRoles.length === 1 && !selectedRole) {
      setSelectedRole(availableRoles[0]);
      setShowRoleSelection(false);
    } else if (availableRoles.length > 1 && !selectedRole) {
      setShowRoleSelection(true);
    } else if (selectedRole) {
      // Role already selected, hide selection UI
      setShowRoleSelection(false);
    }
  }, [availableRoles, selectedRole, setSelectedRole]);

  // Clear loading state once roles are ready to display
  useEffect(() => {
    if (rolesJustFetched && availableRoles.length > 0) {
      const isReadyToDisplay =
        availableRoles.length > 1 ||
        (availableRoles.length === 1 && selectedRole !== null);

      if (isReadyToDisplay) {
        const timer = setTimeout(() => {
          setIsCheckingRoles(false);
          setRolesJustFetched(false);
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, [rolesJustFetched, availableRoles.length, selectedRole]);

  // Get role IDs from selected primary service when needed
  useEffect(() => {
    // Treat search-mode placeholder as empty
    const isSearchModePlaceholder =
      selectedRoleIds.length === 1 && selectedRoleIds[0] === 'search-mode';
    const hasNoRoles = selectedRoleIds.length === 0;

    if (
      (hasNoRoles || isSearchModePlaceholder) &&
      selectedPrimaryService &&
      !selectedRole &&
      services.length > 0
    ) {
      setIsCheckingRoles(true);
      const service = services.find((s) => s._id === selectedPrimaryService);

      if (
        service?._contactRoleId &&
        Array.isArray(service._contactRoleId) &&
        service._contactRoleId.length > 0
      ) {
        setSelectedRoleIds(service._contactRoleId);
        setRolesJustFetched(true);
      } else {
        setIsCheckingRoles(false);
      }
    }
  }, [
    selectedRoleIds,
    selectedPrimaryService,
    selectedRole,
    services,
    setSelectedRoleIds,
  ]);

  const handleEmailSearch = async () => {
    const formatError = validateSingleEmailAddress(email, { required: true });
    if (formatError !== true) {
      setEmailSearchError(formatError);
      return;
    }
    setEmailSearchError(null);

    // Skip email search if contact already loaded from draft
    if (contact && contact.email === email) {
      setShowFullForm(true);
      return;
    }

    setIsSearching(true);
    setCcFoundContacts([]);
    setShowCcConfirmation(false);

    try {
      try {
        const primaryContact = await callServerAction<ContactType>({
        url: `contact/email/${encodeURIComponent(email)}`,
        method: 'get',
        params: {
          _companyId: companyId,
        },
        headers: {
          'client-token': jwtToken,
        },
      });

      if (primaryContact) {
        setFoundContact(primaryContact);
        setValue('firstName', primaryContact.firstName || '');
        setValue('lastName', primaryContact.lastName || '');
        setValue('email', primaryContact.email || email);
        setValue('phone', formatPhoneForDisplay(primaryContact.phone) || '');
        setShowFullForm(true);
        return;
      }
      } catch (error: any) {
        // Primary email not found — try cc-email search
      }

      try {
        const ccContacts = await callServerAction<ContactType[]>({
          url: `contact/cc-email/${encodeURIComponent(email)}`,
          method: 'get',
          params: {
            _companyId: companyId,
          },
          headers: {
            'client-token': jwtToken,
          },
        });

        const contacts = Array.isArray(ccContacts) ? ccContacts : [];
        if (contacts.length > 0) {
          setCcFoundContacts(contacts);
          setShowCcConfirmation(true);
          return;
        }
      } catch (error: any) {
        // CC email not found — fall through to new contact form
      }

      // Not found by primary or CC — show full form for new contact
      setFoundContact(null);
      setValue('firstName', '');
      setValue('lastName', '');
      setValue('email', email);
      setValue('phone', '');
      setShowFullForm(true);
    } finally {
      setIsSearching(false);
    }
  };

  // Helper function to format phone number to E.164
  const formatPhone = (phone: string | undefined): string | undefined => {
    if (!phone || !phone.trim()) return undefined;
    try {
      const phoneNumber = parsePhoneNumber(phone, 'US');
      if (phoneNumber && phoneNumber.isValid()) {
        return phoneNumber.format('E.164');
      }
      return phone.trim();
    } catch {
      return phone.trim();
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    setIsSaving(true);
    try {
      if (!data.email || !data.firstName || !data.lastName) {
        setIsSaving(false);
        return;
      }
      // Format phone number before processing
      const formattedPhone = formatPhone(data.phone);

      // Save contact to context
      let contactToCheck: ContactType | Partial<ContactType>;
      // Check if we have an existing contact (either from foundContact or from context)
      const existingContact =
        foundContact || (contact && contact._id ? contact : null);

      if (existingContact && existingContact._id) {
        // Check if any fields have changed
        const hasChanges =
          existingContact.firstName !== data.firstName ||
          existingContact.lastName !== data.lastName ||
          existingContact.email !== data.email ||
          existingContact.phone !== formattedPhone;

        if (hasChanges) {
          // Update existing contact with form data only if something changed
          try {
            const updatedContact = await callServerAction<ContactType>({
              url: `contact/${existingContact._id}`,
              method: 'patch',
              data: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: formattedPhone,
              },
              params: {
                _companyId: companyId,
              },
              headers: {
                'client-token': jwtToken,
              },
            });
            // Update both foundContact and context contact with the updated data
            setFoundContact(updatedContact);
            setContact(updatedContact);
            contactToCheck = updatedContact;
          } catch (error: any) {
            console.error('Failed to update contact:', error);
            // If update fails, still use the contact with updated fields in context
            const updatedContact = {
              ...existingContact,
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phone: formattedPhone,
            };
            setFoundContact(updatedContact as ContactType);
            setContact(updatedContact as ContactType);
            contactToCheck = updatedContact as ContactType;
          }
        } else {
          // No changes, just use the existing contact
          setContact(existingContact);
          contactToCheck = existingContact;
        }
      } else {
        // New contact - try to find by email first (in case it was created elsewhere)
        // This ensures we can check for existing quotes
        let contactWithId: ContactType | null = null;

        try {
          contactWithId = await callServerAction<ContactType>({
            url: `contact/email/${encodeURIComponent(data.email)}`,
            method: 'get',
            params: {
              _companyId: companyId,
            },
            headers: {
              'client-token': jwtToken,
            },
          });
        } catch (error: any) {
          // Contact not found - that's fine, it's a new contact
          // We'll create it when the draft is saved
        }

        if (contactWithId) {
          // Contact exists - use it and check for quotes
          setContact(contactWithId);
          contactToCheck = contactWithId;
        } else {
          // Truly new contact - create it immediately via API
          try {
            const createdContact = await callServerAction<ContactType>({
              url: 'contact',
              method: 'post',
              data: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phone: formattedPhone,
                submittedBy: 'external',
                preferredContactMethod: ['email', 'text'],
                _companyId: companyId,
              },
              params: {
                _companyId: companyId,
              },
              headers: {
                'client-token': jwtToken,
              },
            });

            if (createdContact && createdContact._id) {
              setContact(createdContact);
              contactToCheck = createdContact;
            } else {
              throw new Error('Failed to create contact - no contact returned');
            }
          } catch (error: any) {
            console.error('Failed to create contact:', error);
            // Fallback: create partial contact if API fails
            const newContact: Partial<ContactType> = {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phone: formattedPhone,
              submittedBy: 'external',
            };
            setContact(newContact as ContactType);
            contactToCheck = newContact;
          }
        }
      }

      // Check for existing DRAFT quotes after setting contact
      try {
        await checkForExistingQuotes(contactToCheck);
        // Don't call nextStep here - checkForExistingQuotes handles it
        // If quotes are found, modal will open; if not, nextStep is called inside checkForExistingQuotes
      } catch (error) {
        // On error, just proceed to next step
        setIsCheckingDrafts(false);
        nextStep();
      }
    } catch (error) {
      // On error, try to proceed anyway
      setIsSaving(false);
      setIsCheckingDrafts(false);
      // Don't block the user - proceed to next step
      nextStep();
    } finally {
      setIsSaving(false); // Stop loading
    }
  };

  // Check for existing DRAFT quotes and show modal if any
  const checkForExistingQuotes = async (
    contactData: ContactType | Partial<ContactType>
  ) => {
    if (!contactData._id) {
      nextStep();
      return;
    }

    setIsCheckingDrafts(true);
    try {
      // Fetch quotes synchronously to get result immediately
      const quotesToCheck = await checkQuotes(contactData._id);

      // Also update the hook state for consistency (for modal to use)
      await fetchQuotes(contactData._id);

      // Check quotes directly from the result
      if (quotesToCheck.length > 0) {
        // Store quotes for modal to use (in case state hasn't updated yet)
        setQuotesForModal(quotesToCheck);
        setShowQuoteModal(true);
        // Don't call nextStep here - let the modal handle navigation
      } else {
        setIsCheckingDrafts(false);
        nextStep();
      }
    } catch (error: any) {
      setIsCheckingDrafts(false);
      nextStep();
    }
  };

  // Handler for when user selects an existing DRAFT quote
  const handleSelectQuote = (quote: Quote) => {
    // console.log('[ContactForm] handleSelectQuote called:', {
    //   quoteId: quote._id,
    //   slug: quote.slug,
    //   status: quote.status,
    // });
    setShowQuoteModal(false);
    setIsCheckingDrafts(false);
    // Save contact ID to localStorage instead of slug
    if (contact?._id) {
      saveDraftToStorage(contact._id);
    }
    // console.log('[ContactForm] Setting draftQuoteSlug and shouldResumeDraft...');
    setDraftQuoteSlug(quote.slug);
    setShouldResumeDraft(true);
  };

  // Handler for when user wants to start new quote from draft quote modal
  const handleStartNewQuote = async () => {
    setShowQuoteModal(false);
    setIsCheckingDrafts(false);

    // CRITICAL: Clear old draft state BEFORE starting new quote
    // This prevents auto-save from updating the old draft
    if (draftQuoteId) {
      setDraftQuoteId(null);
      setDraftQuoteSlug(null);
      setShouldResumeDraft(false);
    }

    // Clear quotes cache before starting new quote
    clearQuotes();
    setQuotesForModal([]);

    // Now start new quote (this will also clear draft state, but we do it here too for safety)
    await startNewQuote();
    nextStep();
  };

  // Helper to get icon component
  const getIconComponent = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? <IconComponent size={24} /> : null;
  };

  // Determine if we should show role selection UI
  const needsRoles = hasValidQuestions || selectedPrimaryService;
  const rolesAreDetermined =
    !needsRoles || selectedRoleIds.length > 0 || selectedRole !== null;
  const shouldShowRoleSelection = availableRoles.length > 1 && !selectedRole;
  const shouldAutoSelectRole = availableRoles.length === 1 && !selectedRole;

  // Only wait if services aren't loaded yet and we need roles
  const shouldWaitForRoles =
    needsRoles && services.length === 0 && !rolesAreDetermined;

  // Show loader only if services aren't loaded yet
  if (shouldWaitForRoles) {
    return (
      <div className='mx-auto max-w-2xl space-y-6'>
        <div className='flex flex-col items-center justify-center py-12'>
          <Spinner
            size='lg'
            color='primary'
            classNames={{
              circle1: 'border-b-primary',
              circle2: 'border-b-primary',
            }}
            style={{
              color: companyStyles.companyColor || '#006FEE',
            }}
          />
          <p className='mt-4 text-sm text-gray-600'>Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-2xl space-y-6'>
      {/* Role Selection (if multiple roles available) */}
      {showRoleSelection && availableRoles.length > 1 && !selectedRole ? (
        <div className='space-y-4'>
          <div className='text-center'>
            <h3 className='mb-2 text-2xl font-bold text-gray-900'>
              What best describes you?
            </h3>
            <p className='text-gray-600'>
              Select your role to continue with your contact information
            </p>
          </div>

          <div
            className='grid gap-4 sm:grid-cols-2'
            style={companyStyles.cssVars}
          >
            {availableRoles.map((role, index) => (
              <motion.button
                key={role._id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                onClick={() => {
                  setSelectedRole(role);
                  setShowRoleSelection(false);
                }}
                className='group relative overflow-hidden rounded-xl border-2 bg-white p-3 text-left transition-all hover:shadow-lg'
                style={{
                  borderColor: 'rgb(229, 231, 235)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor =
                    companyStyles.companyColor || '#006FEE';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgb(229, 231, 235)';
                }}
              >
                <div className='flex items-start gap-4'>
                  {role.icon && (
                    <div
                      className='shrink-0 transition-colors group-hover:text-[var(--company-secondary)]'
                      style={{
                        color: companyStyles.companyColor || '#006FEE',
                      }}
                    >
                      {getIconComponent(role.icon)}
                    </div>
                  )}
                  <div className='flex-1 space-y-2'>
                    <h3
                      className='text-lg font-semibold text-gray-900 transition-colors group-hover:text-(--company-secondary)'
                      style={
                        {
                          '--company-secondary':
                            companyStyles.companyColor || '#006FEE',
                        } as React.CSSProperties
                      }
                    >
                      {role.name}
                    </h3>
                    {(() => {
                      const desc = getRoleDescription(role.description);
                      return desc ? (
                        <p className='text-sm text-gray-600'>{desc}</p>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div
                  className='absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition-all group-hover:translate-x-1 group-hover:text-(--company-secondary)'
                  style={
                    {
                      '--company-secondary':
                        companyStyles.companyColor || '#006FEE',
                    } as React.CSSProperties
                  }
                >
                  <FaChevronRight size={20} />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Selected Role Display */}
          {selectedRole && (
            <div
              className='rounded-xl border-2 p-4 text-center'
              style={{
                borderColor: companyStyles.companyColor
                  ? `${companyStyles.companyColor}33`
                  : 'rgba(0, 111, 238, 0.2)',
                backgroundColor: companyStyles.companyColor
                  ? `${companyStyles.companyColor}0D`
                  : 'rgba(0, 111, 238, 0.05)',
              }}
            >
              <p className='mb-2 text-sm text-gray-600'>
                You described yourself as:
              </p>
              <div className='flex items-center justify-center gap-3'>
                {selectedRole?.icon && (
                  <div
                    style={{
                      color: companyStyles.companyColor || '#006FEE',
                    }}
                  >
                    {getIconComponent(selectedRole.icon)}
                  </div>
                )}
                <p
                  className='text-lg font-semibold'
                  style={{
                    color: companyStyles.companyColor || '#006FEE',
                  }}
                >
                  {selectedRole?.name}
                </p>
              </div>
            </div>
          )}

          {showCcConfirmation && ccFoundContacts.length > 0 ? (
            /* CC Email Confirmation Step — horizontal list of matches */
            <div className='space-y-4'>
              <div className='text-center'>
                <h3 className='mb-2 text-2xl font-bold text-gray-900'>
                  {ccFoundContacts.length === 1
                    ? 'Is this you?'
                    : 'Which one is you?'}
                </h3>
                <p className='text-gray-600'>
                  {ccFoundContacts.length === 1
                    ? 'We found a contact with your email. Please confirm to continue.'
                    : 'We found multiple contacts with your email. Select yours or create a new contact.'}
                </p>
              </div>
              <div
                className={
                  ccFoundContacts.length > 1
                    ? 'flex flex-col divide-y divide-gray-200'
                    : 'flex flex-col'
                }
              >
                {ccFoundContacts.map((ccContact) => (
                  <div
                    key={ccContact._id}
                    className='flex flex-row items-center gap-3 py-3 first:pt-0'
                  >
                    <Avatar
                      src={
                        ccContact.thumb || ccContact.image || undefined
                      }
                      name={
                        [ccContact.firstName, ccContact.lastName]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                      className='h-12 w-12 shrink-0 text-lg'
                    />
                    <p className='min-w-0 flex-1 text-base font-semibold text-gray-900'>
                      {[ccContact.firstName, ccContact.lastName]
                        .filter(Boolean)
                        .join(' ') || 'Unknown'}
                    </p>
                    <Button
                      size='sm'
                      className={`shrink-0 ${companyStyles.secondaryButtonClassName}`}
                      style={companyStyles.secondaryButtonStyles}
                      onPress={() => {
                        setFoundContact(ccContact);
                        setValue('firstName', ccContact.firstName || '');
                        setValue('lastName', ccContact.lastName || '');
                        setValue('email', ccContact.email || email);
                        setValue(
                          'phone',
                          formatPhoneForDisplay(ccContact.phone) || ''
                        );
                        setShowCcConfirmation(false);
                        setCcFoundContacts([]);
                        setShowFullForm(true);
                      }}
                    >
                      Yes, that&apos;s me
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                variant='bordered'
                className='w-full'
                onPress={() => {
                  setShowCcConfirmation(false);
                  setCcFoundContacts([]);
                  setFoundContact(null);
                  setValue('email', email);
                  setValue('firstName', '');
                  setValue('lastName', '');
                  setValue('phone', '');
                  setShowFullForm(true);
                }}
              >
                None of these — create new contact
              </Button>
            </div>
          ) : !showFullForm ? (
            /* Email Search Step */
            <div className='space-y-4'>
              <div className='text-center'>
                <h3 className='mb-2 text-2xl font-bold text-gray-900'>
                  Let&apos;s see if we&apos;ve worked together before
                </h3>
                <p className='text-gray-600'>
                  Enter your email address to check our records
                </p>
              </div>

              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  handleEmailSearch();
                }}
                className='space-y-4'
              >
                <Input
                  type='email'
                  label='Email Address'
                  placeholder='your.email@example.com'
                  value={email}
                  onValueChange={(value) => {
                    setEmail(value);
                    if (emailSearchError) setEmailSearchError(null);
                  }}
                  startContent={<FaEnvelope className='text-gray-400' />}
                  variant='bordered'
                  isRequired
                  isInvalid={!!emailSearchError}
                  errorMessage={emailSearchError ?? undefined}
                  classNames={{
                    inputWrapper: 'border bg-white shadow-none',
                    label: 'font-light uppercase text-xs',
                    input: 'text-base md:text-sm',
                  }}
                />

                <Button
                  type='submit'
                  size='lg'
                  className={`w-full ${companyStyles.secondaryButtonClassName}`}
                  style={companyStyles.secondaryButtonStyles}
                  isLoading={isSearching}
                  isDisabled={!email.trim()}
                  endContent={!isSearching && <FaArrowRight />}
                >
                  {isSearching ? 'Searching...' : 'Continue'}
                </Button>
              </form>
            </div>
          ) : /* Only show form if we're not checking for roles */
          isCheckingRoles ? null : (
            /* Full Form Step */
            <div className='space-y-4'>
              <div className='text-center'>
                {foundContact ? (
                  <>
                    <h3 className='mb-2 text-2xl font-bold text-green-600'>
                      Welcome back!
                    </h3>
                    <p className='text-gray-600'>
                      We found your information in our system.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className='mb-2 text-2xl font-bold text-gray-900'>
                      Nice to meet you!
                    </h3>
                    <p className='text-gray-600'>
                      Please complete your information to continue
                    </p>
                  </>
                )}
              </div>

              {foundContact && (
                <p className='mb-4 text-center text-xs text-gray-500'>
                  Not you? Please use a different email address or contact
                  support.
                </p>
              )}

              <ContactFormFields
                control={control}
                errors={errors}
                isValid={isValid}
                onSubmit={handleSubmit(onSubmit)}
                onCancel={() => {
                  setShowFullForm(false);
                  setEmail('');
                  setFoundContact(null);
                  setCcFoundContacts([]);
                  setShowCcConfirmation(false);
                  reset();
                }}
                cancelLabel='Back'
                submitLabel='Continue'
                showCancel={true}
                isLoading={isSaving}
                isReadOnly={!!foundContact}
              />
            </div>
          )}
        </>
      )}

      {/* Quote Selection Modal - Shows existing DRAFT quotes only */}
      {showQuoteModal && (quotes.length > 0 || quotesForModal.length > 0) && (
        <DraftSelectionModal
          drafts={quotes.length > 0 ? quotes : quotesForModal}
          isOpen={showQuoteModal}
          onSelectDraft={handleSelectQuote}
          onStartNew={handleStartNewQuote}
          onClose={() => {
            setShowQuoteModal(false);
            setQuotesForModal([]);
            setIsCheckingDrafts(false); // Clear flag - user closed modal, allow saves
            // If they close the modal, just proceed to next step
            nextStep();
          }}
          currentDraftId={draftQuoteId}
        />
      )}
    </div>
  );
}
